import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useUserStore } from '@/store/useUserStore';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useAuthModals } from '@/composables/useAuthModals';
import { useReactionsStore } from '../store/useReactionsStore';

export type AddReactionEvent = { name: string };

export const useReactions = () => {
  const itemStore = useViewerItemStore();
  const userStore = useUserStore();
  const reactionsStore = useReactionsStore();

  const { hideBranding, reactionsDisabled } = storeToRefs(itemStore);
  const { isLoggedIn } = storeToRefs(userStore);
  const { track } = useTrack();
  const { showSignUpModal } = useAuthModals();

  const isProcessing = ref(false);

  const countColor = (name: string, index: number, isHovering: number | null) => {
    const isActive = reactionsStore.isActiveReaction(name);

    if (isActive && isHovering == index) return 'text-white';
    if (isActive && isHovering !== index) return 'text-white';
    if (!isActive && isHovering == index) return 'text-black';
    if (!isActive && isHovering !== index) return 'text-black';
  };

  const buttonColor = (name: string, index: number, isHovering: number | null) => {
    const isActive = reactionsStore.isActiveReaction(name);

    if (isActive && isHovering == index) return 'bg-blue-600 ';
    if (isActive && isHovering !== index) return 'bg-blue-500 ';
    if (!isActive && isHovering == index) return 'bg-slate-200 dark:bg-zinc-800';
    if (!isActive && isHovering !== index) return 'bg-slate-100 dark:bg-transparent';
  };

  const onClick = async (id: string | undefined, name: string) => {
    if (isProcessing.value || !id) {
      return;
    }

    isProcessing.value = true;

    try {
      await (reactionsStore.isActiveReaction(name) ? reactionsStore.removeReaction(name) : reactionsStore.addReaction(name, id));

      if (!isLoggedIn?.value && !hideBranding.value) {
        track(TrackEvents.PROMPT_SIGN_UP_REACTION, { reaction: name });
        await showSignUpModal({ sidebarText: 'Join Zight to add your name to the conversation' });
      }
    } finally {
      isProcessing.value = false;
    }
  };

  const load = (id: string) => reactionsStore.load(id);
  return { reactionsDisabled, countColor, buttonColor, onClick, load, reactionsCount: reactionsStore.reactionsCount };
};
